import { AxiosResponse } from "axios";
import {
  CourseEntity,
  CourseEntityInput,
  SearchCourseParams,
  PaginatedCoursesResponse,
  TrimesterEvaluationCourse,
} from "./courses.types";
import { api } from "../api";
import { CourseMinimalResponse } from "../../types/api/getCourse";

export const getCourses = async (params?: SearchCourseParams) => {
  try {
    const searchParams = new URLSearchParams();

    if (params?.page) searchParams.append("page", params.page.toString());
    if (params?.limit) searchParams.append("limit", params.limit.toString());
    if (params?.id) searchParams.append("id", params.id.toString());
    if (params?.title) searchParams.append("title", params.title);
    if (params?.subject_id)
      searchParams.append("subject_id", params.subject_id.toString());
    if (params?.program_code)
      searchParams.append("program_code", params.program_code);

    const url = `/courses/with-projects${
      searchParams.toString() ? `?${searchParams.toString()}` : ""
    }`;
    const response: AxiosResponse<PaginatedCoursesResponse> = await api.get(
      url
    );

    return response.status === 200
      ? response.data
      : {
          data: [],
          pagination: {
            total: 0,
            page: 1,
            limit: 10,
            totalPages: 0,
            hasNext: false,
            hasPrev: false,
          },
        };
  } catch (error: any) {
    console.error(error);
    throw error;
  }
};

export const getAllCourses = async () => {
  try {
    const response: AxiosResponse<CourseMinimalResponse[]> = await api.get(
      "/courses/all"
    );
    return response.status === 200 ? response.data : [];
  } catch (error: any) {
    console.error(error);
    throw error;
  }
};

export const getTrimesterEvaluationCourses = async (
  programId: number,
  trimester: number
) => {
  try {
    const response: AxiosResponse<TrimesterEvaluationCourse[]> = await api.get(
      "/courses/trimester-evaluation-courses",
      { params: { programId, trimester } }
    );
    return response.status === 200 ? response.data : [];
  } catch (error: any) {
    console.error(error);
    throw error;
  }
};

export const getCourseById = async (id: number) => {
  try {
    const response: AxiosResponse<CourseEntity> = await api.get(
      "/courses/" + id
    );
    return response.status === 200 ? response.data : null;
  } catch (error: any) {
    console.error(error);
    throw error;
  }
};

export const createCourse = async (data: CourseEntityInput) => {
  try {
    const response: AxiosResponse<CourseEntity> = await api.post(
      "/courses",
      data
    );

    return response.status === 201 ? response.data : null;
  } catch (error: any) {
    console.error(error);
    throw error;
  }
};

export const updateCourse = async (id: number, data: CourseEntityInput) => {
  try {
    const response: AxiosResponse<CourseEntity> = await api.patch(
      "/courses/" + id,
      data
    );

    return response.status === 200 ? response.data : null;
  } catch (error: any) {
    console.error(error);
    throw error;
  }
};

export const deleteCourse = async (id: number) => {
  try {
    const response: AxiosResponse<CourseEntity> = await api.delete(
      "/courses/" + id
    );

    return response.status === 200 ? response.data : null;
  } catch (error: any) {
    console.error(error);
    throw error;
  }
};

// --- Course-title rename cascade (SCRUM-797) ---
// PATCH /courses/:id returns an additive titleCascadePreview (see
// CourseEntity consumers casting the raw updateCourse() response) whenever
// the title actually changed. Mirrors the backend contract in
// emmi-backend/src/course-title-cascade/course-title-cascade.service.ts.

export type CascadeEntity = "lesson" | "resource";
export type CascadeExclusionReason =
  | "shared_across_courses"
  | "title_not_matching";
export type CascadeSkipReason = "title_changed" | "no_longer_single_course";

export interface CascadeRename {
  entity: CascadeEntity;
  id: number;
  current_title: string;
  new_title: string;
}

export interface CascadeExclusion {
  entity: CascadeEntity;
  id: number;
  title: string;
  reason: CascadeExclusionReason;
  course_count?: number;
}

export interface TitleCascadePreview {
  course: { id: number; old_title: string; new_title: string };
  renames: CascadeRename[];
  excluded: CascadeExclusion[];
}

export interface ConfirmedRename {
  entity: CascadeEntity;
  id: number;
  expected_current_title: string;
  new_title: string;
}

export interface AppliedRename {
  entity: CascadeEntity;
  id: number;
  new_title: string;
}

export interface SkippedRename {
  entity: CascadeEntity;
  id: number;
  reason: CascadeSkipReason;
}

export interface ConfirmTitleCascadeResponse {
  applied: AppliedRename[];
  skipped: SkippedRename[];
}

export const confirmTitleCascade = async (
  courseId: number,
  renames: ConfirmedRename[]
): Promise<ConfirmTitleCascadeResponse | null> => {
  try {
    const response: AxiosResponse<ConfirmTitleCascadeResponse> =
      await api.post(`/admin/content/courses/${courseId}/title-cascade/confirm`, {
        renames,
      });

    return response.status === 200 ? response.data : null;
  } catch (error: any) {
    console.error(error);
    throw error;
  }
};
